'use client';

import * as Diff from 'diff';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CitingRegulation } from '@/lib/enabling-clause';
import { LegalTextFormatter, parseTextIntoSegments } from '@/components/ui/LegalTextFormatter';
import { cn } from '@/lib/utils';

function normalizeForComparison(text: string): string {
  return text
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .trim();
}

function getAyatStatuses(oldText: string, newText: string) {
  const oldSegments = parseTextIntoSegments(oldText);
  const newSegments = parseTextIntoSegments(newText);

  const oldMap = new Map<string, string>();
  oldSegments.forEach((seg) => {
    if (seg.id) oldMap.set(seg.id, seg.content);
    seg.listItems?.forEach((item) => oldMap.set(item.id, item.content));
  });

  const newMap = new Map<string, string>();
  newSegments.forEach((seg) => {
    if (seg.id) newMap.set(seg.id, seg.content);
    seg.listItems?.forEach((item) => newMap.set(item.id, item.content));
  });

  const oldStatuses: Record<string, 'modified' | 'removed'> = {};
  const newStatuses: Record<string, 'modified' | 'added'> = {};

  oldMap.forEach((content, id) => {
    if (!newMap.has(id)) {
      oldStatuses[id] = 'removed';
    } else if (normalizeForComparison(newMap.get(id) || '') !== normalizeForComparison(content)) {
      oldStatuses[id] = 'modified';
    }
  });

  newMap.forEach((content, id) => {
    if (!oldMap.has(id)) {
      newStatuses[id] = 'added';
    } else if (normalizeForComparison(oldMap.get(id) || '') !== normalizeForComparison(content)) {
      newStatuses[id] = 'modified';
    }
  });

  return { oldStatuses, newStatuses };
}

// Global lock to prevent virtualizer or browser from jumping the scroll
let scrollLockCount = 0;
let origScrollBy: typeof window.scrollBy | null = null;
let origScrollTo: typeof window.scrollTo | null = null;

function lockWindowScroll() {
  if (typeof window === 'undefined') return;
  if (scrollLockCount === 0) {
    origScrollBy = window.scrollBy;
    origScrollTo = window.scrollTo;
    window.scrollBy = () => {};
    window.scrollTo = () => {};
  }
  scrollLockCount++;
}

function unlockWindowScroll() {
  if (typeof window === 'undefined') return;
  if (scrollLockCount > 0) {
    scrollLockCount--;
    if (scrollLockCount === 0 && origScrollBy && origScrollTo) {
      window.scrollBy = origScrollBy;
      window.scrollTo = origScrollTo;
      origScrollBy = null;
      origScrollTo = null;
    }
  }
}

function useScrollLockedToggle() {
  const [showDiff, setShowDiff] = useState(false);
  const isTogglingRef = useRef(false);
  const savedScrollYRef = useRef(0);

  const handleToggle = useCallback(() => {
    savedScrollYRef.current = window.scrollY;
    isTogglingRef.current = true;
    setShowDiff((prev) => !prev);
  }, []);

  useLayoutEffect(() => {
    if (isTogglingRef.current) {
      isTogglingRef.current = false;
      const savedY = savedScrollYRef.current;

      const forceScroll = () => {
        if (origScrollTo) {
          origScrollTo.call(window, 0, savedY);
        } else {
          window.scrollTo({ left: 0, top: savedY, behavior: 'instant' });
        }
      };

      lockWindowScroll();
      forceScroll();

      const onScroll = () => {
        if (window.scrollY !== savedY) {
          forceScroll();
        }
      };

      window.addEventListener('scroll', onScroll, { passive: false });

      // Keep scroll locked long enough for the virtualizer's ResizeObserver to settle
      const timer = setTimeout(() => {
        window.removeEventListener('scroll', onScroll);
        unlockWindowScroll();
        forceScroll();
      }, 150);

      return () => {
        window.removeEventListener('scroll', onScroll);
        unlockWindowScroll();
        clearTimeout(timer);
      };
    }
  }, [showDiff]);

  return { showDiff, handleToggle };
}

interface DiffResult {
  value: string;
  added?: boolean;
  removed?: boolean;
}

interface WordToken {
  word: string;
  normalized: string;
  start: number;
  end: number;
}

function extractWordTokens(text: string): WordToken[] {
  const tokens: WordToken[] = [];
  const regex = /[a-zA-Z0-9]+/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    tokens.push({
      word: match[0],
      normalized: match[0].toLowerCase(),
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  return tokens;
}

function processUnchangedTokens(
  oldTokens: WordToken[],
  newTokens: WordToken[],
  oldText: string,
  newText: string,
  startIndex: number,
  count: number,
  results: DiffResult[],
  lastOldEnd: { value: number },
  lastNewEnd: { value: number }
): void {
  for (let i = 0; i < count; i++) {
    const oldToken = oldTokens[startIndex + i];
    const newToken = newTokens[lastNewEnd.value < newText.length ? startIndex + i : -1];

    if (oldToken && oldToken.start > lastOldEnd.value) {
      const oldGap = oldText.slice(lastOldEnd.value, oldToken.start);
      const newGapStart = newToken ? lastNewEnd.value : newText.length;
      const newGapEnd = newToken ? newToken.start : newText.length;
      const newGap = newText.slice(newGapStart, newGapEnd);

      results.push({ value: oldGap === newGap ? oldGap : newGap });
    }

    if (oldToken) {
      results.push({ value: oldToken.word });
      lastOldEnd.value = oldToken.end;
    }

    if (newToken) {
      lastNewEnd.value = newToken.end;
    }
  }
}

function diffWordsIgnoreFormatting(oldText: string, newText: string): DiffResult[] {
  const oldTokens = extractWordTokens(oldText);
  const newTokens = extractWordTokens(newText);

  const oldWords = oldTokens.map((t) => t.normalized);
  const newWords = newTokens.map((t) => t.normalized);

  const wordDiffs = Diff.diffArrays(oldWords, newWords);

  const results: DiffResult[] = [];
  let oldTokenIndex = 0;
  let newTokenIndex = 0;
  const lastOldEnd = { value: 0 };
  const lastNewEnd = { value: 0 };

  for (const diff of wordDiffs) {
    const count = diff.value.length;

    if (diff.added) {
      for (let i = 0; i < count; i++) {
        const token = newTokens[newTokenIndex];
        if (!token) continue;

        if (token.start > lastNewEnd.value) {
          results.push({ value: newText.slice(lastNewEnd.value, token.start) });
        }

        results.push({ value: token.word, added: true });
        lastNewEnd.value = token.end;
        newTokenIndex++;
      }
    } else if (diff.removed) {
      for (let i = 0; i < count; i++) {
        const token = oldTokens[oldTokenIndex];
        if (!token) continue;

        if (token.start > lastOldEnd.value) {
          results.push({ value: oldText.slice(lastOldEnd.value, token.start) });
        }

        results.push({ value: token.word, removed: true });
        lastOldEnd.value = token.end;
        oldTokenIndex++;
      }
    } else {
      processUnchangedTokens(
        oldTokens, newTokens, oldText, newText, oldTokenIndex, count, results, lastOldEnd, lastNewEnd
      );
      oldTokenIndex += count;
      newTokenIndex += count;
    }
  }

  if (lastOldEnd.value < oldText.length) {
    const remainingOld = oldText.slice(lastOldEnd.value);
    const remainingNew = newText.slice(lastNewEnd.value);

    if (remainingOld === remainingNew) {
      results.push({ value: remainingOld });
    } else if (remainingNew) {
      results.push({ value: remainingNew });
    }
  } else if (lastNewEnd.value < newText.length) {
    results.push({ value: newText.slice(lastNewEnd.value) });
  }

  const mergedResults: DiffResult[] = [];
  for (const result of results) {
    if (
      mergedResults.length > 0 &&
      !mergedResults[mergedResults.length - 1].added &&
      !mergedResults[mergedResults.length - 1].removed &&
      !result.added &&
      !result.removed
    ) {
      mergedResults[mergedResults.length - 1].value += result.value;
    } else {
      mergedResults.push({ ...result });
    }
  }

  return mergedResults;
}

function DiffChangedWord({
  oldText,
  newText,
  className,
}: {
  oldText: string;
  newText: string;
  className?: string;
}) {
  const changes: DiffResult[] = diffWordsIgnoreFormatting(oldText, newText);

  return (
    <div className={cn('leading-relaxed text-sm', className)}>
      {changes.map((part: DiffResult, index: number) => {
        if (part.added) {
          return (
            <span
              key={index}
              className="bg-green-100 text-green-800 px-0.5 rounded-sm border-b-2 border-green-400 font-medium"
            >
              {part.value}
            </span>
          );
        }
        if (part.removed) {
          return (
            <span
              key={index}
              className="bg-red-100 text-red-700 line-through px-0.5 rounded-sm opacity-70"
            >
              {part.value}
            </span>
          );
        }
        return <span key={index}>{part.value}</span>;
      })}
    </div>
  );
}

interface DiffInlineProps {
  oldText: string;
  newText: string;
  className?: string;
  regulationId?: string;
  consolidationId?: string;
  citingRegulations?: CitingRegulation[];
  hasPremiumRole?: boolean;
}

export function DiffInline({
  oldText,
  newText,
  className,
  regulationId,
  consolidationId,
  citingRegulations,
  hasPremiumRole,
}: DiffInlineProps) {
  const { showDiff, handleToggle } = useScrollLockedToggle();

  return (
    <div className={cn('space-y-3', className)} style={{ overflowAnchor: 'none' }}>
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleToggle}
        className="flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800 transition-colors"
      >
        <svg
          className={cn('w-4 h-4 transition-transform', showDiff && 'rotate-90')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {showDiff ? 'Sembunyikan Perubahan' : 'Lihat Perubahan'}
      </button>

      {showDiff ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-4">
          <div className="flex gap-4 text-xs font-medium uppercase tracking-wide mb-3">
            <span className="flex items-center gap-1.5 text-red-600">
              <span className="w-2 h-2 rounded-full bg-red-400" />
              Dihapus
            </span>
            <span className="flex items-center gap-1.5 text-green-600">
              <span className="w-2 h-2 rounded-full bg-green-400" />
              Ditambahkan
            </span>
          </div>
          <DiffChangedWord oldText={oldText} newText={newText} />
        </div>
      ) : (
        <div className="text-sm text-gray-900 leading-relaxed">
          <LegalTextFormatter
            text={newText}
            enableArticleReferences={!!regulationId}
            regulationId={regulationId}
            consolidationId={consolidationId}
            citingRegulations={citingRegulations}
            hasPremiumRole={hasPremiumRole}
          />
        </div>
      )}
    </div>
  );
}

interface DiffSideBySideProps {
  oldText: string;
  newText: string;
  className?: string;
  regulationId?: string;
  consolidationId?: string;
  query?: string;
  highlightClassName?: string;
  citingRegulations?: CitingRegulation[];
  hasPremiumRole?: boolean;
}

export function DiffSideBySide({
  oldText,
  newText,
  className,
  regulationId,
  consolidationId,
  query,
  highlightClassName,
  citingRegulations,
  hasPremiumRole,
}: DiffSideBySideProps) {
  const { showDiff, handleToggle } = useScrollLockedToggle();

  const { oldStatuses, newStatuses } = React.useMemo(
    () => getAyatStatuses(oldText, newText),
    [oldText, newText]
  );

  return (
    <div className={cn('space-y-3', className)} style={{ overflowAnchor: 'none' }}>
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleToggle}
        className="flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800 transition-colors"
      >
        <svg
          className={cn('w-4 h-4 transition-transform', showDiff && 'rotate-90')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {showDiff ? 'Sembunyikan Perbandingan' : 'Lihat Perubahan'}
      </button>

      {showDiff && (
        <>
          {/* Desktop Inline View */}
          <div className="hidden lg:grid grid-cols-2 gap-4 pt-2" style={{ overflowAnchor: 'none' }}>
            <div className="col-span-2 text-xs text-amber-600 mb-1 flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Ayat yang mengalami perubahan ditandai dengan warna kuning
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50/30 overflow-hidden">
              <div className="bg-red-100 px-3 py-1.5 border-b border-red-200 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                  Sebelum
                </span>
              </div>
              <div className="p-3 text-gray-600 leading-relaxed">
                <LegalTextFormatter
                  text={oldText}
                  enableArticleReferences={!!regulationId}
                  regulationId={regulationId}
                  consolidationId={consolidationId}
                  itemStatuses={oldStatuses}
                  query={query}
                  highlightClassName={highlightClassName}
                  citingRegulations={citingRegulations}
                  hasPremiumRole={hasPremiumRole}
                />
              </div>
            </div>

            <div className="rounded-lg border border-green-200 bg-green-50/30 overflow-hidden">
              <div className="bg-green-100 px-3 py-1.5 border-b border-green-200 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                  Sesudah
                </span>
              </div>
              <div className="p-3 text-gray-900 leading-relaxed">
                <LegalTextFormatter
                  text={newText}
                  enableArticleReferences={!!regulationId}
                  regulationId={regulationId}
                  consolidationId={consolidationId}
                  itemStatuses={newStatuses}
                  query={query}
                  highlightClassName={highlightClassName}
                  citingRegulations={citingRegulations}
                  hasPremiumRole={hasPremiumRole}
                />
              </div>
            </div>
          </div>

          {/* Mobile Fullscreen Portal View */}
          {typeof document !== 'undefined' &&
            createPortal(
              <div
                className="fixed inset-0 z-[100] flex flex-col bg-gray-100 lg:hidden"
                style={{ overflowAnchor: 'none' }}
              >
                {/* Mobile Modal Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shrink-0 shadow-sm">
                  <span className="font-semibold text-gray-900">Perbandingan Perubahan</span>
                  <button
                    onClick={handleToggle}
                    className="p-2 -mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="text-xs text-amber-600 flex items-center gap-1.5 shrink-0 px-4 py-2 bg-amber-50 border-b border-amber-200">
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Ayat yang mengalami perubahan ditandai dengan warna kuning
                </div>

                {/* Split Container */}
                <div className="flex flex-col flex-1 overflow-hidden">
                  {/* Sebelum */}
                  <div className="flex flex-col flex-1 min-h-0 bg-white overflow-hidden relative">
                    <div className="bg-red-100 px-3 py-2 border-b border-red-200 flex items-center gap-2 shrink-0 sticky top-0 z-10 shadow-sm">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                        Sebelum
                      </span>
                    </div>
                    <div className="p-4 text-gray-600 leading-relaxed overflow-y-auto flex-1">
                      <LegalTextFormatter
                        text={oldText}
                        enableArticleReferences={!!regulationId}
                        regulationId={regulationId}
                        consolidationId={consolidationId}
                        itemStatuses={oldStatuses}
                        query={query}
                        highlightClassName={highlightClassName}
                        citingRegulations={citingRegulations}
                        hasPremiumRole={hasPremiumRole}
                      />
                    </div>
                  </div>

                  {/* Divider for Mobile */}
                  <div className="h-2 bg-gray-200 shrink-0 border-y border-gray-300 shadow-inner flex items-center justify-center">
                    <div className="w-12 h-1 bg-gray-300 rounded-full" />
                  </div>

                  {/* Sesudah */}
                  <div className="flex flex-col flex-1 min-h-0 bg-white overflow-hidden relative">
                    <div className="bg-green-100 px-3 py-2 border-b border-green-200 flex items-center gap-2 shrink-0 sticky top-0 z-10 shadow-sm">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                        Sesudah
                      </span>
                    </div>
                    <div className="p-4 text-gray-900 leading-relaxed overflow-y-auto flex-1">
                      <LegalTextFormatter
                        text={newText}
                        enableArticleReferences={!!regulationId}
                        regulationId={regulationId}
                        consolidationId={consolidationId}
                        itemStatuses={newStatuses}
                        query={query}
                        highlightClassName={highlightClassName}
                        citingRegulations={citingRegulations}
                        hasPremiumRole={hasPremiumRole}
                      />
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )}
        </>
      )}

      {!showDiff && (
        <div className="text-sm text-gray-900 leading-relaxed">
          <LegalTextFormatter
            text={newText}
            enableArticleReferences={!!regulationId}
            regulationId={regulationId}
            consolidationId={consolidationId}
            query={query}
            highlightClassName={highlightClassName}
            citingRegulations={citingRegulations}
            hasPremiumRole={hasPremiumRole}
          />
        </div>
      )}
    </div>
  );
}

interface DiffTextProps {
  oldText: string;
  newText: string;
  className?: string;
}

export function DiffText({ oldText, newText, className }: DiffTextProps) {
  return <DiffInline oldText={oldText} newText={newText} className={className} />;
}
